/**
 * Web port of Coupons/CouponsListAll.
 *
 * Dropped from the mobile version (mobile-specific, no clean web equivalent):
 *  - useFeedImpressions / trackClick dwell-time analytics (viewability
 *    tracking is a FlatList/RN concept; a web analog would need an
 *    IntersectionObserver-based impressions hook built separately).
 *  - AppState background/foreground retry logic (browser tabs don't get
 *    backgrounded the same way iOS/Android apps do).
 *
 * Everything else — category/subcategory filtering, the city picker,
 * favourites toggle, search, and pagination — is preserved.
 *
 * DESIGN UPDATE: the top filter area matches the reference exactly — TWO
 * separate rows, not a merged bar. Row 1 is its own full-width white pill:
 * a search icon + free-text search input. Row 2 is three separate
 * auto-width chips (not stretched/merged): City (map-pin icon, via
 * CityPickerButtonModal's default 'chip' variant), Category (a small 2×2
 * dot-grid icon + label + chevron, pushed apart via space-between), and
 * Favourites (heart icon). No state, handlers, or redux wiring changed —
 * only markup/classNames in the render + the icon imports below.
 *
 * CATEGORY DROPDOWN DESIGN — restyled to match Home's hero category
 * dropdown exactly: a 2-col pill grid instead of a stacked list, with
 * "All categories" as a full-width pill (Grid icon) on its own row and
 * the currently-selected category highlighted via the `.active` class.
 * See CouponsListAll.module.scss .dropdown / .dropdown-item /
 * .dropdown-item-all for the shared tokens with Home.module.scss.
 *
 * CATEGORY ICONS — `dynamicCategories` here is the raw, full list from
 * getCategoryList() (unlike Home's exploreCategories, which is a manually
 * curated 5-item subset matched against local PNGs). getCategoryIcon()
 * below resolves an icon per-category with a 3-step fallback:
 *   1. An image field on the category object itself, if the API returns
 *      one (checks `image`, `icon`, `icon_url` — first one present wins).
 *   2. Home's same local 5-icon map (FALLBACK_CATEGORY_ICONS), matched by
 *      category name, for parity when categories overlap with Home's set.
 *   3. No icon — falls back to text-only pill (previous behavior).
 * If your API has a known image field name, simplify step 1 to just that
 * field once confirmed.
 *
 * MULTI-CITY SEARCH HANDOFF — GuestHome and Home both pass a category
 * picked before navigating here via `navigate(RouteNames.COUPONS_LISTS_ALL,
 * { state: { preSelectedCategories: [...] } })`; that's consumed by the
 * effect below. They now also forward the confirmed multi-city selection
 * the same way, via `location.state.preSelectedCities` (an array of
 * `{ id, name }` objects — the same shape `selectedCities`/`pendingCities`
 * already use here). Both are consumed once on mount, then cleared from
 * history state so a refresh or back/forward nav doesn't keep reapplying
 * them. The city read explicitly marks `hasInitialisedCities` so the
 * separate "seed from active city" effect (which only ever knows about a
 * single redux city) doesn't collapse the incoming multi-city selection
 * back down to one city.
 *
 * HERO IMAGE — new full-bleed hero behind the search/filter tools,
 * matching the guest homepage's hero treatment. The image is resolved
 * from `activeCity` (the single redux-tracked "current" city — the same
 * value the header pill and city bootstrap on Home/GuestHome use), NOT
 * from `selectedCities` (which can hold several cities from the chip
 * picker and has no single sensible image).
 *
 * City photos live in public/images/cities/ (e.g. "Halifax.jpg"), NOT
 * under src/ — CRA copies public/ as-is without running it through
 * webpack, so there's no build-time way to import or enumerate what's in
 * there (require.context only sees src/ files). Instead, useCityHeroImage
 * below builds an ordered candidate URL list — the active city against
 * each known extension, then "Canada" against each extension as the
 * final fallback — and the <img>'s onError handler walks to the next
 * candidate whenever one 404s, landing on whichever file actually
 * exists. Add a "Canada.<ext>" file to public/images/cities/ as the
 * catch-all for any city without a matching photo.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import cx from 'classnames';
import CSSModule from 'react-css-modules';
import { ChevronDown, ChevronUp, Grid, Heart, Search as SearchIcon } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import TrackedCouponCard from '../../../components/TrackedCouponCard/TrackedCouponCard'; 
import { AppWrapper, CityPickerButtonModal,CustomerCouponCard, Spinner, SeoHead } from 'components';
import { SITE_URL } from 'components/SeoHead/SeoHead';
import { actions as appliedActions } from 'coupon-common/src/modules/customerCoupons/applied';
import { actions as categoryActions } from 'coupon-common/src/modules/customerCoupons/categories';
import { actions as categorizedActions } from 'coupon-common/src/modules/customerCoupons/categorized';
import { actions as favouriteActions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { actions as cityActions } from 'coupon-common/src/modules/cities';
import { actions as locationActions } from 'coupon-common/src/modules/locations';

import foodDrinkIcon from '../../../assets/images/categories/CategoryFoodDrink.webp';
import selfCareIcon from '../../../assets/images/categories/CategorySelfCare.webp';
import servicesIcon from '../../../assets/images/categories/CategoryServices.webp';
import shoppingIcon from '../../../assets/images/categories/CategoryShopping.webp';
import thingsToDoIcon from '../../../assets/images/categories/CategoryThingsToDo.webp';
import { RouteNames } from '../../../constants';
import styles from './CouponsListAll.module.scss';

const FEED_ID = 'search';

// Same localStorage key + fallback city as GuestHome/index.js — used
// below only as a last resort when this screen is reached with no
// activeCity already set in redux (e.g. a direct link/refresh landing
// here, rather than navigating over from Home/GuestHome, which already
// set one).
const CACHE_KEYS = {
  DEFAULT_CITY: 'DEFAULT_CITY',
};
const GUEST_DEFAULT_CITY = { id: 347, name: 'Halifax' };

// City hero images live in public/images/cities/ (not src/), so they're
// referenced by URL rather than import — see file header for why. Tried
// in this order per extension.
const CITY_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const CITY_IMAGES_BASE = `${process.env.PUBLIC_URL}/images/cities`;

// Builds the ordered list of candidate URLs for a given city: the city
// name against every extension, then "Canada" against every extension as
// the final fallback. Exact filename casing on disk doesn't matter on
// case-insensitive filesystems (macOS/Windows dev), but on a
// case-sensitive prod host (most Linux-based static hosts) the city name
// here is used exactly as `activeCity` provides it — rename files to
// match if that's ever an issue.
const buildCityImageCandidates = cityName => {
  const candidates = [];
  if (cityName) {
    CITY_IMAGE_EXTENSIONS.forEach(ext => candidates.push(`${CITY_IMAGES_BASE}/${cityName}.${ext}`));
  }
  CITY_IMAGE_EXTENSIONS.forEach(ext => candidates.push(`${CITY_IMAGES_BASE}/Canada.${ext}`));
  return candidates;
};

/**
 * Resolves the hero <img> src for a city with a client-side fallback
 * chain: starts at the first candidate (cityName.jpg), and on each
 * onError advances to the next (cityName.jpeg, ...png, ...webp, then
 * Canada.jpg, Canada.jpeg, ...) until one actually loads. Resets to the
 * start whenever cityName changes.
 */
const useCityHeroImage = cityName => {
  
  const candidates = useMemo(() => buildCityImageCandidates(cityName), [cityName]);
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [candidates]);

  const handleError = useCallback(() => {
    setCandidateIndex(i => Math.min(i + 1, candidates.length - 1));
  }, [candidates]);

  return { src: candidates[candidateIndex], onError: handleError };
};

/**
 * SESSION-SCOPED FETCH CACHE — plain module-scope variable, NOT
 * sessionStorage/localStorage. See GuestHome/index.js for the full
 * rationale on why this survives unmount/remount but resets on a hard
 * refresh.
 *
 * This screen needs a bit more than Home/GuestHome's single `activeCityId`
 * cache: its fetch key (`queryKey` below) is derived from *local* filter
 * state (selectedCategories/selectedSubCategories/selectedCities), and
 * local state always resets to its defaults on remount before any effect
 * gets a chance to restore it. If we only cached the queryKey itself,
 * the very first render after a remount would compute a transient,
 * wrong queryKey from the just-reset (empty) filters and incorrectly
 * treat that as "input changed" — triggering an unwanted reset+refetch
 * before the real filters caught back up a tick later. So this cache
 * seeds those local states synchronously via each useState's lazy
 * initializer, so the very first render after remount already has the
 * same filters (and therefore the same queryKey) it had before — letting
 * the guard below correctly recognize "nothing changed" and skip the
 * refetch, same as Home/GuestHome's simpler city-only check.
 */
let couponsListAllCache = null; // { selectedCategories, selectedSubCategories, selectedCities, queryKey }

// Same local fallback icons as Home's categoryIcons map — adjust these
// import paths if Home's actual asset filenames differ from the ones
// guessed here (Home.js imports them as foodDrinkIcon/thingsToDoIcon/
// shoppingIcon/selfCareIcon/servicesIcon from '../../assets/images/...';
// point these at the exact same files).
const FALLBACK_CATEGORY_ICONS = {
  'Food & Drink': foodDrinkIcon,
  'Things To Do': thingsToDoIcon,
  Shopping: shoppingIcon,
  'Self Care': selfCareIcon,
  Services: servicesIcon,
};

/**
 * Resolves an icon for a category with a 3-step fallback: an image field
 * on the category object itself (if the API provides one), then Home's
 * local icon map matched by name, then null (text-only pill).
 */
const getCategoryIcon = cat => {
  if (!cat) return null;
  const apiImage = cat.image || cat.icon || cat.icon_url;
  if (apiImage) return apiImage;
  return FALLBACK_CATEGORY_ICONS[cat.name] || null;
};

/**
 * Renders its children into document.body at fixed coordinates under
 * `triggerRef`, instead of `position: absolute` inside the normal DOM flow —
 * so it can't get clipped by an ancestor's overflow (e.g. the filter strip's
 * horizontal scroll) or buried under the app's sticky header / side rail.
 */
const DropdownPortal = ({ triggerRef, portalRef, isOpen, children }) => {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    const updateCoords = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, left: rect.left, minWidth: rect.width });
    };
    updateCoords();

    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [isOpen, triggerRef]);

  if (!isOpen || !coords) return null;

  return createPortal(
    <div
      ref={portalRef}
      className={styles.dropdown}
      style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.minWidth }}
    >
      {children}
    </div>,
    document.body,
  );
};

/**
 * Small 2×2 dot-grid icon for the Category chip — matches the reference
 * exactly; react-feather doesn't ship this shape (its closest icon, Grid,
 * renders squares in a 3×3 layout instead). Purely presentational.
 */
const CategoryIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <circle cx="4.5" cy="4.5" r="1.75" fill="currentColor" />
    <circle cx="11.5" cy="4.5" r="1.75" fill="currentColor" />
    <circle cx="4.5" cy="11.5" r="1.75" fill="currentColor" />
    <circle cx="11.5" cy="11.5" r="1.75" fill="currentColor" />
  </svg>
);

const CouponsListAll = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const allState = useSelector(state => state.customerCoupons.categorized);
  const { fetching, fetched, items, result, error } = allState;
  const { results, nextPage } = result || {};

  const followState = useSelector(state => state.businesses.follow);
  const allFavourite = useSelector(state => state.customerCoupons.favourite);
  const favouriteList = allFavourite && allFavourite.data;

  const appliedState = useSelector(state => state.customerCoupons.applied);

  const categoryState = useSelector(state => state.customerCoupons.categories || {});
  const dynamicCategories = (categoryState.data && categoryState.data.results) || categoryState.data || [];

  const activeCityId = useSelector(state => state.cities.activeCityId);
  const activeCity = useSelector(state => state.cities.activeCity);

  // -------------------- LOCAL STATE --------------------
  const [searchKey, setSearchKey] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(() => couponsListAllCache?.selectedCategories ?? []);
  const [selectedSubCategories, setSelectedSubCategories] = useState(() => couponsListAllCache?.selectedSubCategories ?? []);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false);
  const [isFavoritesActive, setIsFavoritesActive] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // City picker: pendingCities is what the modal works against; it only
  // commits to selectedCities (which drives the actual fetch) on confirm.
  // Both are seeded from the session cache (see note above) so a
  // navigate-away-and-back doesn't visibly reset the city picker back to
  // a single city before re-settling on whatever was actually selected.
  const [selectedCities, setSelectedCities] = useState(() => couponsListAllCache?.selectedCities ?? []);
  const [pendingCities, setPendingCities] = useState(() => couponsListAllCache?.selectedCities ?? []);
  const hasInitialisedCities = useRef(!!(couponsListAllCache?.selectedCities && couponsListAllCache.selectedCities.length > 0));
  const hasInitialisedCategory = useRef(false);

  const cityLabel = selectedCities.length > 0 ? selectedCities.map(c => c.name).join(', ') : 'Select Location';

  // Hero image for this page — keyed off activeCity (the single
  // redux-tracked "current" city), not selectedCities (see file header).
  const heroImage = useCityHeroImage(activeCity);

  const nextPageRef = useRef(null);
  // Seeded from the session cache: if this exact filter combination was
  // already fetched earlier in the session, this starts out equal to the
  // queryKey that gets (re)computed below from the cache-restored filters
  // above — so the reset+fetch effect further down correctly recognizes
  // "nothing changed" on mount and skips re-fetching. On a true cold
  // start (no cache yet) this is null, which never matches a real
  // queryKey string, so the initial fetch still happens as normal.
  const lastQueryKeyRef = useRef(couponsListAllCache?.queryKey ?? null);
  const resultsLengthRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const categoryDropdownRef = useRef(null);
  const subCategoryDropdownRef = useRef(null);
  const categoryPortalRef = useRef(null);
  const subCategoryPortalRef = useRef(null);
  const sentinelRef = useRef(null);

  // -------------------- SET ACTIVE CITY IF NULL --------------------
  // Same auto-location chain as GuestHome/index.js: localStorage (explicit
  // prior choice) > nearest-city-by-IP (first-time visitor) > hardcoded
  // GUEST_DEFAULT_CITY (lookup failed/empty). Only runs when this screen
  // is reached with no activeCity already in redux — normally Home/
  // GuestHome have already set one before navigating here, so the guard
  // below (`if (activeCityId) return`) keeps this from ever overriding
  // that existing choice, e.g. on a direct link or a refresh of this page.
  const nearestCityState = useSelector(
    state => (state.locations && state.locations.nearestCity) || { fetching: false, fetched: false, data: null, error: null },
  );
  const nearestCityDispatched = useRef(false);

  useEffect(() => {
    if (activeCityId) return;

    try {
      const stored = localStorage.getItem(CACHE_KEYS.DEFAULT_CITY);
      if (stored) {
        const parsed = JSON.parse(stored);
        dispatch(cityActions.setActiveCity(parsed.name, parsed.id));
        return;
      }
    } catch (e) {
      console.warn('Could not read default city from localStorage', e);
      dispatch(cityActions.setActiveCity(GUEST_DEFAULT_CITY.name, GUEST_DEFAULT_CITY.id));
      return;
    }

    // No stored city — ask the backend (via saga) to resolve one from the visitor's IP.
    nearestCityDispatched.current = true;
    dispatch(locationActions.getNearestCity());
  }, [activeCityId, dispatch]);

  // Reacts once the nearest-city saga above resolves (success or failure).
  useEffect(() => {
    if (!nearestCityDispatched.current || !nearestCityState.fetched) return;

    if (nearestCityState.data && nearestCityState.data.id) {
      const { id, name } = nearestCityState.data;
      dispatch(cityActions.setActiveCity(name, id));
      try {
        localStorage.setItem(CACHE_KEYS.DEFAULT_CITY, JSON.stringify({ name, id }));
      } catch (e) {
        console.warn('Could not persist nearest city to localStorage', e);
      }
    } else {
      console.warn('Nearest-city lookup failed, using guest default', nearestCityState.error);
      dispatch(cityActions.setActiveCity(GUEST_DEFAULT_CITY.name, GUEST_DEFAULT_CITY.id));
    }

    // Stops this effect from re-firing on unrelated nearestCityState changes later.
    nearestCityDispatched.current = false;
  }, [nearestCityState, dispatch]);

  // -------------------- SEED CITY FROM ACTIVE CITY --------------------
  useEffect(() => {
    if (!activeCityId || !activeCity) return;

    if (!hasInitialisedCities.current) {
      const initial = [{ id: activeCityId, name: activeCity }];
      setSelectedCities(initial);
      setPendingCities(initial);
      hasInitialisedCities.current = true;
    } else {
      setSelectedCities(prev => (prev.some(c => c.id === activeCityId) ? prev : [...prev, { id: activeCityId, name: activeCity }]));
      setPendingCities(prev => (prev.some(c => c.id === activeCityId) ? prev : [...prev, { id: activeCityId, name: activeCity }]));
    }
  }, [activeCityId, activeCity]);

  // -------------------- SEED CATEGORY + CITIES FROM INCOMING NAVIGATION STATE --------------------
  // GuestHome (and Home) pass a category picked before navigating here via
  // `navigate(RouteNames.COUPONS_LISTS_ALL, { state: { preSelectedCategories: [...] } })`,
  // and now do the same for the confirmed multi-city selection via
  // `preSelectedCities` (an array of `{ id, name }`, same shape this
  // screen's own city picker already uses). Consume both once on mount,
  // then clear them from history state so a page refresh or back/forward
  // navigation doesn't keep silently reapplying the same filters forever.
    useEffect(() => {
    if (hasInitialisedCategory.current) return;
    hasInitialisedCategory.current = true;

    // Hand-off from the Next.js landing page: router state can't cross apps, so it arrives as URL params.
    const params = new URLSearchParams(location.search);
    let urlCategories = [];
    let urlCities = [];
    try {
      urlCategories = (params.get('categories') || '').split(',').map(Number).filter(Boolean);
      const rawCities = params.get('cities');
      if (rawCities) {
        const parsed = JSON.parse(rawCities);
        if (Array.isArray(parsed)) urlCities = parsed.filter(c => c && c.id && c.name);
      }
    } catch (e) {
      // ignore malformed params
    }

    const incomingCategories = location.state?.preSelectedCategories || (urlCategories.length > 0 ? urlCategories : undefined);
    const incomingCities = location.state?.preSelectedCities || (urlCities.length > 0 ? urlCities : undefined);

    if (Array.isArray(incomingCategories) && incomingCategories.length > 0) {
      setSelectedCategories(incomingCategories);
      setSelectedSubCategories([]);
    }

    if (Array.isArray(incomingCities) && incomingCities.length > 0) {
      setSelectedCities(incomingCities);
      setPendingCities(incomingCities);
      hasInitialisedCities.current = true;
    }

    if ((incomingCategories && incomingCategories.length > 0) || (incomingCities && incomingCities.length > 0)) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.search, location.pathname, navigate]);

  // -------------------- NAVIGATION --------------------
  const goToDetails = (couponId, business) => {
    navigate(`${RouteNames.COUPONS_DETAILS}/${couponId}`, {
      state: { businessLocation: business?.location ?? null },
    });
  };

  const goToBusiness = useCallback(
    obj => {
      if (obj && obj.id && !obj.business) {
        navigate(`${RouteNames.BUSINESSES_DETAILS}/${obj.id}`);
        return;
      }
      if (obj?.business?.id) {
        const targetRoute = obj.is_affiliate_or_flyer ? RouteNames.AFFILIATE_BUSINESS_DETAILS : RouteNames.BUSINESSES_DETAILS;
        navigate(`${targetRoute}/${obj.business.id}`);
      }
    },
    [navigate],
  );

  // -------------------- LOAD CATEGORIES + APPLIED COUPONS ON MOUNT --------------------
  useEffect(() => {
    dispatch(categoryActions.getCategoryList());
    dispatch(appliedActions.getApplyCouponList());
  }, [dispatch]);

  // -------------------- LIST BUILDING --------------------
  const list = useMemo(() => {
    if (!results || !items) return [];
    return results.map(res => items[res]).filter(Boolean);
  }, [results, items]);

  // -------------------- FAVORITES FILTER --------------------
  const favoriteCoupons = useMemo(() => {
    if (!favouriteList || !favouriteList.results) return [];

    let favList = Array.isArray(favouriteList.results) ? favouriteList.results : [];

    if (isFavoritesActive && selectedCategories.length > 0) {
      favList = favList.filter(coupon => coupon.category && selectedCategories.includes(coupon.category.id));
    }

    return favList;
  }, [favouriteList, selectedCategories, isFavoritesActive]);

  // -------------------- SEARCH + CATEGORY FILTER --------------------
  const filteredList = useMemo(() => {
    let templist = [...list];

    if (searchKey.trim()) {
      const lowerSearchKey = searchKey.toLowerCase();
      templist = templist.filter(item => {
        const titleMatch = item.title && item.title.toLowerCase().includes(lowerSearchKey);
        const descriptionMatch = item.description && item.description.toLowerCase().includes(lowerSearchKey);
        return titleMatch || descriptionMatch;
      });
    }

    if (selectedCategories.length === 0) return templist;

    if (selectedSubCategories.length > 0) {
      templist = templist.filter(coupon => {
        const subcategoryIds = (coupon.subcategories && coupon.subcategories.map(s => s.id)) || [];
        return subcategoryIds.some(id => selectedSubCategories.includes(id));
      });
    }

    return templist.filter(coupon => {
      if (!coupon.category) return false;
      return selectedCategories.includes(coupon.category.id);
    });
  }, [list, searchKey, selectedCategories, selectedSubCategories]);

  // -------------------- QUERY KEY (drives fetch + pagination reset) --------------------
  const cityDep = useMemo(() => (selectedCities || []).map(c => c.id).sort((a, b) => a - b).join(','), [selectedCities]);

const queryKey = useMemo(() => {
  return JSON.stringify({
    cat: [...selectedCategories].sort((a, b) => a - b),
    sub: [...selectedSubCategories].sort((a, b) => a - b),
    cities: cityDep,
  });
}, [selectedCategories, selectedSubCategories, cityDep]);

  // Keep the session cache (module scope, see declaration above) in sync
  // with the current filters + derived queryKey, so a later remount in
  // this same browser session — navigating away and back — can restore
  // this exact state instead of resetting to defaults.
  useEffect(() => {
    couponsListAllCache = {
      selectedCategories,
      selectedSubCategories,
      selectedCities,
      queryKey,
    };
  }, [selectedCategories, selectedSubCategories, selectedCities, queryKey]);

  // -------------------- FETCH FUNCTION --------------------
  const getAllListRef = useRef();

  useEffect(() => {
    getAllListRef.current = (params = {}) => {
      dispatch(
        categorizedActions.getBrowseList({
          category_ids: selectedCategories,
          subcategory_ids: selectedSubCategories,
          cities: (selectedCities || []).map(c => c.id),
          page: params.page || 1,
        }),
      );
    };
  }, [selectedCategories, selectedSubCategories, selectedCities, dispatch]);

  // -------------------- TRACK NEXT PAGE / LOADING STATE --------------------
  useEffect(() => {
    if (typeof nextPage !== 'undefined') {
      nextPageRef.current = { key: queryKey, value: nextPage };
    }

    const currentLen = (results && results.length) || 0;
    if (loadingNextPage && currentLen > resultsLengthRef.current) {
      setLoadingNextPage(false);
      isLoadingMoreRef.current = false;
    }
    resultsLengthRef.current = currentLen;

    if (isTransitioning && !fetching) {
      setIsTransitioning(false);
    }
  }, [nextPage, results, loadingNextPage, queryKey, isTransitioning, fetching]);

  // -------------------- QUERY KEY CHANGE -> RESET + REFETCH --------------------
  // lastQueryKeyRef is seeded above from the session cache, so on a
  // same-session remount with unchanged filters this condition is true
  // immediately (no reset, no refetch — the already-loaded/paginated
  // redux data is reused as-is). On a genuine filter change, or a true
  // cold start, it differs and the normal reset+refetch below runs.
  useEffect(() => {
    if (lastQueryKeyRef.current === queryKey) return;

    lastQueryKeyRef.current = queryKey;
    nextPageRef.current = null;
    resultsLengthRef.current = 0;
    setLoadingNextPage(false);
    isLoadingMoreRef.current = false;
    setIsTransitioning(true);
    dispatch(categorizedActions.resetCouponList());
    getAllListRef.current({ page: 1 });
  }, [queryKey, dispatch]);

  // -------------------- SUBCATEGORY OPTIONS --------------------
  // Derived from the category data, NOT from loaded coupons, so the options
  // and the selection stay stable while the filtered list is reset/refetched.
  const dynamicSubcategories = useMemo(() => {
    const cat = dynamicCategories.find(c => c.id === selectedCategories[0]);
    return (cat && cat.subcategories) || [];
  }, [dynamicCategories, selectedCategories]);

  // -------------------- FAVOURITE CHECK / TOGGLE --------------------
  const checkFavourite = useCallback(
    coupon => (favouriteList && favouriteList.results && favouriteList.results.some(favCoupon => favCoupon.id === coupon.id)) || false,
    [favouriteList],
  );

  const toggleFavouriteHandler = (id, isFavourite) => {
    const action = isFavourite ? favouriteActions.removeFavourite(id) : favouriteActions.markAsFavourite(id);
    dispatchAsAsync(action, dispatch);
  };

  // -------------------- USER LIMIT CHECK --------------------
  const reachedUserLimit = couponId => {
    const targetStates = new Set(['reserved', 'accepted', 'waiting']);
    const coupons = appliedState?.data?.results || [];
    const matching = coupons.filter(item => targetStates.has(item.state) && item.coupon === couponId);
    if (matching.length === 0) return false;
    return matching.length >= (matching[0]?.coupon_details?.user_limit ?? Infinity);
  };

  // -------------------- PAGINATION --------------------
  const handleLoadMore = useCallback(() => {
    if (isLoadingMoreRef.current) return;

    const isValidNext = nextPageRef.current && nextPageRef.current.key === queryKey;
    const next = isValidNext ? nextPageRef.current.value : null;

    if (!next) return;

    isLoadingMoreRef.current = true;
    setLoadingNextPage(true);
    getAllListRef.current({ page: next });
  }, [queryKey]);

  // -------------------- RENDER GUARDS --------------------
  // Declared here (rather than just above the JSX) because the infinite
  // scroll effect below needs showLoading/showError to know when the
  // sentinel div has actually mounted.
  const showError = error && !fetched;
  const showLoading = !filteredList || (fetching && !fetched) || isTransitioning;

  // -------------------- INFINITE SCROLL (IntersectionObserver) --------------------
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin: '400px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore, showLoading, showError]);

  // -------------------- CLICK OUTSIDE TO CLOSE DROPDOWNS --------------------
  useEffect(() => {
    const handleClickOutside = e => {
      const insideCategory =
        (categoryDropdownRef.current && categoryDropdownRef.current.contains(e.target)) ||
        (categoryPortalRef.current && categoryPortalRef.current.contains(e.target));
      if (!insideCategory) {
        setIsCategoryOpen(false);
      }

      const insideSubCategory =
        (subCategoryDropdownRef.current && subCategoryDropdownRef.current.contains(e.target)) ||
        (subCategoryPortalRef.current && subCategoryPortalRef.current.contains(e.target));
      if (!insideSubCategory) {
        setIsSubCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // -------------------- DISPLAY DATA --------------------
  const displayData = (isFavoritesActive ? favoriteCoupons : filteredList || []).filter(item => !reachedUserLimit(item.id));


  // ─── SEO ────────────────────────────────────────────────────────────
// NOTE: filters (search/category/city) live in local state, not the URL,
// so `path`/canonical below is always the same generic route regardless
// of what's actually filtered — only title/description/JSON-LD vary.
// Real per-filter indexing would need these reflected as query params.
const activeCategoryName = selectedCategories.length > 0
  ? (dynamicCategories.find(cat => cat.id === selectedCategories[0]) || {}).name
  : null;
const hasCityFilter = cityLabel && cityLabel !== 'Select Location';

const brandSuffix = ' | My Couponbook';
const locationLabel = hasCityFilter ? cityLabel : 'Canada';

const seoTitle = searchKey.trim()
  ? `Search results for "${searchKey.trim()}"${brandSuffix}`
  : `Local ${activeCategoryName ? `${activeCategoryName} ` : ''}Deals in ${locationLabel}${brandSuffix}`;

const seoDescription =
  'Search the latest deals in Canada for places to eat, electronics, beauty products, salons, clothing brands, and local activities. See what is on sale near you.';
// const seoTitle = searchKey.trim()
//   ? `Search results for "${searchKey.trim()}"`
//   : activeCategoryName
//   ? `${activeCategoryName} Coupons${hasCityFilter ? ` in ${cityLabel}` : ''}`
//   : hasCityFilter
//   ? `Coupons & Deals in ${cityLabel}`
//   : 'Search Coupons & Deals';
// const seoDescription = `Browse and search coupons${activeCategoryName ? ` in ${activeCategoryName}` : ''}${
//   hasCityFilter ? ` near ${cityLabel}` : ' across Canada'
// } on My Couponbook.`;
const structuredData =
  displayData.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: displayData.slice(0, 50).map((coupon, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: coupon.title,
          url: `${SITE_URL}${RouteNames.COUPONS_DETAILS}/${coupon.id}`,
        })),
      }
    : undefined;

  return (
    
      <div styleName="page">
          <SeoHead
         title={seoTitle}
         description={seoDescription}
         path={RouteNames.COUPONS_LISTS_ALL}
         type="website"
         structuredData={structuredData}
       />

        {/* Hero — full-bleed image behind the search/filter tools, keyed
            off activeCity with a Canada.jpg fallback. See file header. */}
        <div styleName="hero">
          <img src={heroImage.src} onError={heroImage.onError} alt="" styleName="hero-bg" />
          <div styleName="hero-overlay" />
          <div styleName="hero-inner">
            <p styleName="hero-search-label">Searching for deals in {activeCity || 'Canada'}</p>

        {/* Search section — one single horizontal row: search bar (flexes
            to fill remaining space) + Location / Category / Favourites
            chips (fixed/auto width), all side by side. */}
        <div styleName="search-section">
          <div styleName="search-row">
            <div styleName="search-bar">
              <SearchIcon size={18} styleName="search-icon" />
              <input
                type="text"
                styleName="search-input"
                placeholder="Search For Coupons."
                value={searchKey}
                onChange={e => setSearchKey(e.target.value)}
              />
            </div>

            <div styleName="chip-wrapper">
              <CityPickerButtonModal
              label={cityLabel}
              selectedCities={pendingCities}
              onChangeSelected={setPendingCities}
              onConfirm={() => setSelectedCities(pendingCities)}
              confirmLabel="Search"
            />
          </div>

          <div styleName="chip-wrapper" ref={categoryDropdownRef}>
            <button type="button" styleName="chip chip-category" onClick={() => setIsCategoryOpen(!isCategoryOpen)}>
              <span styleName="chip-icon-text">
                <CategoryIcon size={16} />
                <span styleName="chip-text">
                  {selectedCategories.length > 0
                    ? (dynamicCategories.find(c => c.id === selectedCategories[0]) || {}).name
                    : 'Category'}
                </span>
              </span>
              {isCategoryOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <DropdownPortal triggerRef={categoryDropdownRef} portalRef={categoryPortalRef} isOpen={isCategoryOpen}>
              <button
                type="button"
                styleName={cx('dropdown-item', 'dropdown-item-all', { active: selectedCategories.length === 0 })}
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedSubCategories([]);
                  setIsCategoryOpen(false);
                }}
              >
                <Grid size={15} styleName="dropdown-item-feather-icon" />
                <span>All categories</span>
              </button>
              {dynamicCategories.map(cat => {
                const icon = getCategoryIcon(cat);
                return (
                  <button
                    type="button"
                    key={cat.id}
                    styleName={cx('dropdown-item', { active: selectedCategories.includes(cat.id) })}
                    onClick={() => {
                      setSelectedCategories([cat.id]);
                      setSelectedSubCategories([]);
                      setIsCategoryOpen(false);
                    }}
                  >
                    {icon && <img src={icon} alt="" styleName="dropdown-item-icon" />}
                    <span>{cat.name}</span>
                  </button>
                );
              })}
            </DropdownPortal>
          </div>

          {dynamicSubcategories.length > 0 && (
            <div styleName="chip-wrapper" ref={subCategoryDropdownRef}>
              <button type="button" styleName="chip" onClick={() => setIsSubCategoryOpen(!isSubCategoryOpen)}>
                <span styleName="chip-text">
                  {selectedSubCategories.length > 0
                    ? dynamicSubcategories
                        .filter(sub => selectedSubCategories.includes(sub.id))
                        .map(sub => sub.name)
                        .join(', ')
                    : 'Sub-Categories'}
                </span>
                {isSubCategoryOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              <DropdownPortal triggerRef={subCategoryDropdownRef} portalRef={subCategoryPortalRef} isOpen={isSubCategoryOpen}>
                {dynamicSubcategories.map(sub => {
                  const isSelected = selectedSubCategories.includes(sub.id);
                  return (
                    <button
                      type="button"
                      key={sub.id}
                      styleName={cx('dropdown-item', { active: isSelected })}
                      onClick={() => {
                        setSelectedSubCategories(
                          isSelected ? selectedSubCategories.filter(id => id !== sub.id) : [...selectedSubCategories, sub.id],
                        );
                      }}
                    >
                      <span>{sub.name}</span>
                    </button>
                  );
                })}
              </DropdownPortal>
            </div>
          )}

          <div styleName="chip-wrapper">
            <button
              type="button"
              styleName={isFavoritesActive ? 'chip chip-active' : 'chip'}
              onClick={() => {
                setIsFavoritesActive(!isFavoritesActive);
                if (!isFavoritesActive) {
                  dispatch(favouriteActions.getAllFavouriteList());
                }
              }}
            >
              <Heart size={15} fill={isFavoritesActive ? '#fff' : 'none'} />
              <span styleName="chip-text">Favourites</span>
            </button>
          </div>
          </div>
        </div>
          </div>
        </div>

        {showError && <div styleName="state-message">Something went wrong loading coupons.</div>}

        {showLoading && !showError && (
          <div styleName="state-message">
            <Spinner show size="md" />
          </div>
        )}

        {!showLoading && !showError && (
          <>
            {displayData.length === 0 ? (
              <div styleName="state-message">No coupons found.</div>
            ) : (
              <div styleName="grid">
                  {displayData.map((item, index) => (
                  <TrackedCouponCard
                    key={item.id}
                    item={item}
                    index={index}
                    feedId={FEED_ID}
                    feedSort={
                      isFavoritesActive
                        ? 'favourites'
                        : selectedCategories.length > 0
                        ? 'category'
                        : searchKey.trim()
                        ? 'search_query'
                        : 'browse'
                    }
                    first={index === 0}
                    onPress={() => goToDetails(item.id, item.business)}
                    goToBusinessDetails={() => goToBusiness(item)}
                    toggleFavourite={() => toggleFavouriteHandler(item.id, checkFavourite(item))}
                    isFavourite={checkFavourite(item)}
                  />
                ))}
              </div>
            )}
            <div ref={sentinelRef} styleName="scroll-sentinel" />
            {loadingNextPage && (
              <div styleName="state-message">
                <Spinner show size="sm" />
              </div>
            )}
          </>
        )}
      </div>
    
  );
};

export default CSSModule(CouponsListAll, styles, { allowMultiple: true });